<template>
  <div class="min-h-screen bg-[#FFF7EF]">
    <div class="mx-auto flex min-h-screen max-w-7xl items-center justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
      <section class="w-full max-w-xl rounded-[32px] border border-gray-100 bg-white p-5 shadow-sm sm:p-8 lg:p-10">
          <h2 class="text-2xl font-semibold text-ink sm:text-[2rem]">Log in</h2>
          <p class="mt-2 text-sm leading-7 text-muted sm:text-base">Use your vendor credentials to continue.</p>

          <form class="mt-8 space-y-5" @submit.prevent="handleSubmit">
            <p v-if="error" class="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-semibold text-red-700">
              {{ error }}
            </p>

            <label class="field-block">
              <div class="field-shell" :class="fieldShellClass('username', focusedField === 'username', username)">
                <span class="field-floating-label">Email ID / Phone no*</span>
                <input
                  v-model="username"
                  type="text"
                  required
                  class="field-control"
                  @focus="focusedField = 'username'"
                  @blur="focusedField = ''"
                >
              </div>
            </label>

            <label class="field-block">
              <div class="field-shell" :class="fieldShellClass('password', focusedField === 'password', password)">
                <span class="field-floating-label">Password*</span>
                <input
                  :type="showPassword ? 'text' : 'password'"
                  required
                  v-model="password"
                  class="field-control pr-14"
                  @focus="focusedField = 'password'"
                  @blur="focusedField = ''"
                >
                <button
                  type="button"
                  class="action-btn single-action"
                  @click="showPassword = !showPassword"
                  aria-label="Toggle password visibility"
                >
                  <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
            </label>

            <div class="flex flex-wrap items-center justify-between gap-3 text-xs text-muted">
              <label class="inline-flex items-center gap-2">
                <input type="checkbox" class="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand">
                <span>Keep me signed in</span>
              </label>
              <RouterLink class="font-semibold text-brand" to="/forgot-password">Forgot password?</RouterLink>
            </div>

            <button
              type="submit"
              :disabled="loading"
              class="w-full rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
            >
              {{ loading ? 'Signing in...' : 'Continue to dashboard' }}
            </button>
          </form>

          <div class="mt-8 text-center text-xs text-muted">
            New to Invi8?
            <RouterLink class="font-semibold text-brand" to="/register">Create an account</RouterLink>
          </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { setAuthSession, setAuthToken, setAuthUser } from '@/services/auth'

const router = useRouter()
const route = useRoute()

const username = ref('')
const password = ref('')
const loading = ref(false)
const showPassword = ref(false)
const error = ref('')
const focusedField = ref('')

const fieldShellClass = (key, focused, value) => ({
  'field-shell-active': focused || Boolean(value),
  'field-shell-error': key === 'error' && Boolean(error.value)
})

const handleSubmit = async () => {
  error.value = ''
  loading.value = true
  const formData = new FormData()
  formData.append('s_username', username.value)
  formData.append('s_password', password.value)

  try {
    const { data } = await api.loginVendor(formData)
    const responseItems = Array.isArray(data?.data) ? data.data : []
    const primaryUser = responseItems[0] || null
    const token = data?.token || data?.access_token || data?.data?.token || data?.data?.access_token
    const user =
      data?.user ||
      primaryUser ||
      data?.data?.user ||
      data?.data?.profile ||
      data?.profile ||
      (data?.data && typeof data.data === 'object' ? data.data : null)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    const resolvedUserId =
      user?.s_id ||
      user?.id ||
      user?.user_id ||
      user?.vendor_id ||
      user?.vendorId ||
      ''

    if (isError) {
      throw new Error(data?.msg || data?.message || 'Login failed')
    }

    if (token) {
      setAuthToken(token)
    }

    if (user) {
      setAuthUser({
        ...user,
        s_id: resolvedUserId || user?.s_id || ''
      })
    } else if (username.value) {
      setAuthUser({
        name: username.value,
        username: username.value,
        s_id: resolvedUserId
      })
    }

    setAuthSession({
      loggedIn: true,
      s_id: resolvedUserId
    })

    const redirectPath = route.query.redirect || '/dashboard'
    router.push(redirectPath)
  } catch (err) {
    error.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Login failed'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.field-block {
  display: block;
}

.field-shell {
  position: relative;
  min-height: 72px;
  border: 1px solid #d8dde5;
  border-radius: 18px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-shell-active {
  border-color: #ec2263;
  box-shadow: 0 0 0 4px rgba(236, 34, 99, 0.08);
}

.field-shell-error {
  border-color: #ef4444;
}

.field-floating-label {
  position: absolute;
  left: 18px;
  top: 24px;
  font-size: 16px;
  color: #9ca3af;
  pointer-events: none;
  transition: transform 0.18s ease, top 0.18s ease, font-size 0.18s ease, color 0.18s ease;
}

.field-shell-active .field-floating-label {
  top: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.field-control {
  width: 100%;
  min-height: 72px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  padding: 28px 18px 12px;
  font-size: 16px;
  color: #111827;
}

.field-control:focus {
  outline: none;
}

.single-action {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 999px;
  background: rgba(156, 163, 175, 0.12);
  color: #6b7280;
}
</style>
